// @ts-nocheck — migrated from JS; reactstrap / react-select / layout prop friction.
import React, { Fragment, useMemo } from "react";
import IntlMessages from "Util/IntlMessages";
import { useIntl } from "react-intl";
import {
  Row,
  Card,
  CardBody,
  CardTitle,
  Badge,
  Progress,
  Button
} from "reactstrap";
import { Colxx, Separator } from "Components/CustomBootstrap";
import BreadcrumbContainer from "Components/BreadcrumbContainer";
import PerfectScrollbar from "react-perfect-scrollbar";
import {
  PERFECT_SCROLLBAR_MAX_320,
  PERFECT_SCROLLBAR_MAX_360,
  PERFECT_SCROLLBAR_MAX_420
} from "Constants/perfectScrollbarLayout";
import { SAAS_CRM_TABLE_PROGRESS_CELL } from "Constants/reactifyInlineStyles";
import ReactTable from "react-table";
import DataTablePagination from "Components/DataTables/pagination";
import Chart from "react-apexcharts";
import { Sparklines, SparklinesLine } from "react-sparklines";
import { useKpiSparklineLineStyle } from "Hooks/useKpiSparklineLineStyle";
import { ReactifyChartColors } from "Util/ReactifyChartColors";
import { addCommas } from "Util/Utils";
import data from "Data/dashboardSaasCrm.json";

import "react-table/react-table.css";

function money(n) {
  return `$${addCommas(Math.round(n))}`;
}

function SaasCrmDashboard() {
  const colors = useMemo(() => ReactifyChartColors(), []);
  const kpiSparkLineStyle = useKpiSparklineLineStyle(colors.ReactifyChartColor1);
  const intl = useIntl();

  const dealsColumns = useMemo(() => {
    const { formatMessage } = intl;
    return [
      {
        Header: formatMessage({ id: "saas-crm.col.deal" }),
        accessor: "id",
        Cell: p => <span className="font-weight-medium">{p.value}</span>
      },
      {
        Header: formatMessage({ id: "saas-crm.col.company" }),
        accessor: "company"
      },
      {
        Header: formatMessage({ id: "saas-crm.col.owner" }),
        accessor: "owner"
      },
      {
        Header: formatMessage({ id: "saas-crm.col.stage" }),
        accessor: "stage",
        Cell: p => (
          <Badge color="light" className="text-dark">
            {p.value}
          </Badge>
        )
      },
      {
        Header: formatMessage({ id: "saas-crm.col.value" }),
        accessor: "value",
        Cell: p => money(p.value)
      },
      {
        Header: formatMessage({ id: "saas-crm.col.close" }),
        accessor: "closeDate"
      },
      {
        Header: formatMessage({ id: "saas-crm.col.health" }),
        accessor: "health",
        Cell: p => {
          const c =
            p.value === "green"
              ? "success"
              : p.value === "yellow"
                ? "warning"
                : "danger";
          return (
            <Badge color={c} className="text-uppercase">
              {p.value}
            </Badge>
          );
        }
      }
    ];
    }, [intl]);

  const mrrChartOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false }, zoom: { enabled: false } },
      colors: [c.ReactifyChartColor1, c.ReactifyChartColor2],
      stroke: { width: [3, 2], curve: "smooth" },
      dataLabels: { enabled: false },
      legend: { position: "top", horizontalAlign: "right" },
      xaxis: {
        categories: data.revenueTrend.categories,
        labels: { style: { colors: c.primaryColor } }
      },
      yaxis: {
        labels: {
          style: { colors: c.primaryColor },
          formatter: v => `${v}k`
        }
      },
      grid: { borderColor: c.separatorColor, strokeDashArray: 4 },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const mrrChartSeries = useMemo(
    () => [
      { name: "MRR ($k)", data: data.revenueTrend.mrr },
      { name: "New logo ($k)", data: data.revenueTrend.newLogo }
    ],
    []
  );

  const planMixOptions = useMemo(() => {
    const c = colors;
    return {
      labels: data.planMix.map(p => p.name),
      colors: [
        c.ReactifyChartColor1,
        c.ReactifyChartColor2,
        c.ReactifyChartColor3,
        c.ReactifyChartColor4
      ],
      legend: { position: "bottom" },
      plotOptions: {
        pie: {
          donut: {
            size: "65%",
            labels: {
              show: true,
              total: {
                show: true,
                label: "Plans",
                formatter: () => "100%"
              }
            }
          }
        }
      },
      dataLabels: { enabled: false }
    };
    }, [colors]);

  const pipelineOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false } },
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 4,
          barHeight: "72%",
          distributed: true
        }
      },
      colors: [
        c.ReactifyChartColor1,
        c.ReactifyChartColor2,
        c.ReactifyChartColor3,
        c.ReactifyChartColor4,
        c.ReactifyChartColor5
      ],
      dataLabels: {
        enabled: true,
        formatter: (val, opts) => {
          const row = data.pipeline[opts.dataPointIndex];
          return `${row.count} · ${money(row.value)}`;
        }
      },
      xaxis: {
        categories: data.pipeline.map(p => p.stage),
        labels: { style: { colors: c.primaryColor } }
      },
      yaxis: { labels: { style: { colors: c.primaryColor } } },
      grid: { borderColor: c.separatorColor },
      legend: { show: false },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const { formatMessage } = intl;
  const k = data.kpis;

  return (
      <div className="dashboard-vertical dashboard-vertical--saas-crm">
        <Fragment>
          <Row>
            <Colxx xxs="12">
              <BreadcrumbContainer
                heading={<IntlMessages id="menu.saas-crm" />}
              />
              <Separator className="mb-5" />
            </Colxx>
          </Row>

          <Row>
            <Colxx xxs="12" className="mb-3">
              {data.alerts.map((a, i) => (
                <div
                  key={i}
                  className={`alert alert-${a.level === "warning" ? "warning" : "info"} py-2 mb-2`}
                  role="status"
                >
                  {a.text}
                </div>
              ))}
            </Colxx>
          </Row>

          <Row>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <Sparklines data={data.mrrSpark} margin={6}>
                    <SparklinesLine style={kpiSparkLineStyle} />
                  </Sparklines>
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="saas-crm.kpi.mrr" />
                  </p>
                  <h4 className="mb-0">{money(k.mrr)}</h4>
                  <span className="text-success small">+{k.mrrDelta}%</span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-chart-line text-primary mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="saas-crm.kpi.arr" />
                  </p>
                  <h4 className="mb-0">{money(k.arr)}</h4>
                  <span className="text-muted small">NRR {k.nrr}%</span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-percent text-danger mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="saas-crm.kpi.churn" />
                  </p>
                  <h4 className="mb-0">{k.churn}%</h4>
                  <span className="text-muted small">
                    <IntlMessages id="saas-crm.kpi.logo" /> +{money(k.expansion)}
                  </span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-building text-info mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="saas-crm.kpi.accounts" />
                  </p>
                  <h4 className="mb-0">{addCommas(k.activeAccounts)}</h4>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-flask text-warning mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="saas-crm.kpi.trials" />
                  </p>
                  <h4 className="mb-0">{k.trials}</h4>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-random text-secondary mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="saas-crm.kpi.pipeline-value" />
                  </p>
                  <h4 className="mb-0">
                    {money(
                      data.pipeline.reduce((s, p) => s + p.value, 0)
                    )}
                  </h4>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-trophy text-success mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="saas-crm.kpi.won-deals" />
                  </p>
                  <h4 className="mb-0">
                    {data.pipeline.find(p => p.stage === "Closed Won")?.count ||
                      0}
                  </h4>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <p className="text-muted mb-1 small text-uppercase">
                        <IntlMessages id="saas-crm.kpi.quick-actions" />
                      </p>
                      <Button color="primary" size="sm" className="mt-1">
                        <IntlMessages id="saas-crm.action.new-deal" />
                      </Button>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="8" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="saas-crm.chart.mrr-trend" />
                  </CardTitle>
                  <Chart
                    options={mrrChartOptions}
                    series={mrrChartSeries}
                    type="line"
                    height={320}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="4" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="saas-crm.chart.plan-mix" />
                  </CardTitle>
                  <Chart
                    options={planMixOptions}
                    series={data.planMix.map(p => p.value)}
                    type="donut"
                    height={320}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="5" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="saas-crm.chart.pipeline" />
                  </CardTitle>
                  <Chart
                    options={pipelineOptions}
                    series={[
                      {
                        data: data.pipeline.map(p => p.count)
                      }
                    ]}
                    type="bar"
                    height={340}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="4" className="mb-4">
              <Card className="h-100">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="saas-crm.section.accounts" />
                  </CardTitle>
                  <PerfectScrollbar
                    style={PERFECT_SCROLLBAR_MAX_360}
                    options={{ suppressScrollX: true, wheelPropagation: false }}
                  >
                    <table className="table table-sm mb-0">
                      <thead>
                        <tr>
                          <th>
                            {formatMessage({ id: "saas-crm.col.account" })}
                          </th>
                          <th>{formatMessage({ id: "saas-crm.col.tier" })}</th>
                          <th>{formatMessage({ id: "saas-crm.col.health" })}</th>
                          <th>{formatMessage({ id: "saas-crm.col.seats" })}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.accounts.map((a, i) => (
                          <tr key={i}>
                            <td className="font-weight-medium">{a.name}</td>
                            <td>
                              <Badge color="secondary" pill>
                                {a.tier}
                              </Badge>
                            </td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <Progress
                                  value={a.health}
                                  style={SAAS_CRM_TABLE_PROGRESS_CELL}
                                />
                                <span className="small">{a.health}</span>
                              </div>
                            </td>
                            <td>{addCommas(a.seats)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </PerfectScrollbar>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="3" className="mb-4">
              <Card className="h-100">
                <CardBody className="d-flex flex-column">
                  <CardTitle>
                    <IntlMessages id="saas-crm.section.tasks" />
                  </CardTitle>
                  <PerfectScrollbar
                    className="flex-grow-1"
                    style={PERFECT_SCROLLBAR_MAX_320}
                    options={{ suppressScrollX: true, wheelPropagation: false }}
                  >
                    <ul className="list-unstyled mb-0">
                      {data.tasksDue.map((t, i) => (
                        <li
                          key={i}
                          className="border-bottom pb-2 mb-2 text-small"
                        >
                          <Badge
                            color={
                              t.priority === "high"
                                ? "danger"
                                : t.priority === "medium"
                                  ? "warning"
                                  : "light"
                            }
                            className="me-2"
                          >
                            {t.priority}
                          </Badge>
                          {t.title}
                          <div className="text-muted mt-1">{t.due}</div>
                        </li>
                      ))}
                    </ul>
                  </PerfectScrollbar>
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="8" className="mb-4">
              <Card>
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="saas-crm.table.deals" />
                  </CardTitle>
                  <ReactTable
                    defaultPageSize={6}
                    data={data.deals}
                    columns={dealsColumns}
                    minRows={0}
                    PaginationComponent={DataTablePagination}
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="4" className="mb-4">
              <Card className="h-100">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="saas-crm.section.activity" />
                  </CardTitle>
                  <PerfectScrollbar
                    style={PERFECT_SCROLLBAR_MAX_420}
                    options={{ suppressScrollX: true, wheelPropagation: false }}
                  >
                    <ul className="list-unstyled mb-0">
                      {data.activities.map((act, i) => (
                        <li
                          key={i}
                          className="d-flex mb-3 pb-3 border-bottom"
                        >
                          <span className="text-muted small text-nowrap me-3">
                            {act.time}
                          </span>
                          <div>
                            <Badge color="light" className="me-2 text-dark">
                              {act.type}
                            </Badge>
                            {act.text}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </PerfectScrollbar>
                </CardBody>
              </Card>
            </Colxx>
          </Row>
        </Fragment>
      </div>
    );
}

export default SaasCrmDashboard;
